import { useLocation } from "wouter";
import Breadcrumbs from "@/components/Breadcrumbs";

const NON_PUBLIC_PREFIXES = ["/admin", "/unsubscribed"] as const;

function isBlogArticle(path: string) {
  return /^\/blog\/(?!search(?:\/|$))[^/]+/.test(path);
}

function hasInlineBreadcrumbs(path: string) {
  return (
    path === "/ar" ||
    path.startsWith("/ar/") ||
    isBlogArticle(path) ||
    path.startsWith("/services") ||
    path.startsWith("/case-studies") ||
    path.startsWith("/aeo") ||
    (path === "/local" || path.startsWith("/local/")) ||
    path.startsWith("/resources") ||
    path.startsWith("/tools") ||
    path.startsWith("/methodology") ||
    path.startsWith("/provider-scope-card") ||
    path.startsWith("/how-b2b-brands-appear-in-chatgpt-answers") ||
    path.startsWith("/new-cat-parent-essentials") ||
    path.startsWith("/modi-elnadi") ||
    path.startsWith("/faq") ||
    path === "/contact" ||
    path.startsWith("/editorial-policy") ||
    path.startsWith("/ai-marketing-news") ||
    path.startsWith("/google-ads-optimization-ppc-performance") ||
    path.startsWith("/b2b-lead-generation-strategies-best") ||
    path.startsWith("/ai-search-ppc-agencies-london-comparison") ||
    path.startsWith("/b2b-marketing-agencies-london-pricing") ||
    path.startsWith("/ai-marketing-agency-london") ||
    path.startsWith("/ai-website-agency-london") ||
    path.startsWith("/marketing-agency-wimbledon")
  );
}

function hasPageOwnedBreadcrumbSchema(path: string) {
  return (
    path === "/blog" ||
    isBlogArticle(path) ||
    path === "/free-ai-growth-audit" ||
    (path === "/local" || path.startsWith("/local/")) ||
    path.startsWith("/services") ||
    path.startsWith("/case-studies") ||
    path.startsWith("/aeo") ||
    path.startsWith("/resources") ||
    path.startsWith("/tools") ||
    path.startsWith("/methodology") ||
    path.startsWith("/provider-scope-card") ||
    path.startsWith("/how-b2b-brands-appear-in-chatgpt-answers") ||
    path.startsWith("/new-cat-parent-essentials") ||
    path.startsWith("/modi-elnadi") ||
    path.startsWith("/faq") ||
    path.startsWith("/editorial-policy") ||
    path.startsWith("/ai-marketing-news") ||
    path.startsWith("/google-ads-optimization-ppc-performance") ||
    path.startsWith("/b2b-lead-generation-strategies-best") ||
    path.startsWith("/ai-search-ppc-agencies-london-comparison") ||
    path.startsWith("/b2b-marketing-agencies-london-pricing") ||
    path.startsWith("/pricing") ||
    path.startsWith("/contact") ||
    path.startsWith("/ai-marketing-agency-london") ||
    path.startsWith("/ai-website-agency-london") ||
    path.startsWith("/marketing-agency-wimbledon")
  );
}

/**
 * Consistent breadcrumb fallback for public English routes. Pages with an
 * editorially placed trail retain it; this component contributes a matching
 * BreadcrumbList only where their page graph does not already provide one.
 */
export default function GlobalBreadcrumbs() {
  const [location] = useLocation();
  const path = location.replace(/\/$/, "") || "/";

  if (
    path === "/" ||
    path === "/404" ||
    NON_PUBLIC_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)) ||
    path === "/ar" ||
    path.startsWith("/ar/")
  ) {
    return null;
  }

  const showVisualTrail = !hasInlineBreadcrumbs(path);
  const includeSchema = !hasPageOwnedBreadcrumbSchema(path);

  if (!showVisualTrail && !includeSchema) return null;

  if (!showVisualTrail) {
    return <Breadcrumbs includeSchema renderVisual={false} />;
  }

  return (
    <div
      data-breadcrumb-shell="global"
      style={{
        position: "fixed",
        top: "64px",
        left: 0,
        right: 0,
        zIndex: 90,
        background: "rgba(15,20,25,0.92)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 16px" }}>
        <Breadcrumbs includeSchema={includeSchema} />
      </div>
    </div>
  );
}
